import { isMeasure, Nullable, NullableMeasure } from '@e2m-customer-portal/shared';
import { Typography, TypographyProps } from '@mui/material';
import { isNil } from 'ramda';
import { memo, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FormattedValueProps {
  /**
   * An optional class-name to apply to the value container.
   */
  className?: string;

  /**
   * The html element and formatting to use.
   */
  variant?: 'raw' | TypographyProps['variant'];

  /**
   * The value to format. May be a string or number. The number will be formatted according to the
   * currently selected language. If no value is given, nothing is rendered.
   */
  value: Nullable<NullableMeasure | number | string>;

  /**
   * Maximum number of fractional digits. Defaults to three.
   */
  maximumFractionDigits?: number;

  /**
   * Minimum of fractional digits. Defaults to none.
   */
  minimumFractionDigits?: number;

  /**
   * Sets number of fractional digits, both minimum and maximum.
   */
  fractionDigits?: number;

  /**
   * An optional unit for the value. Units are separated by a narrow non-breaking space. If the `value` is a
   * `Measure`, this unit replaces the unit of the value.
   */
  unit?: string;

  /**
   * If set to true, the unit will be omitted, even if it was given via a `Measure` or via the `unit` property.
   */
  hideUnit?: boolean;

  /**
   * An optional prefix that gets prepended to the formatted value. As it's part of the formatted text, it will
   * inherit the same formatting.
   */
  prefix?: ReactNode;

  /**
   * An optional suffix that gets appended to the formatted value. As it's part of the formatted text, it will
   * inherit the same formatting.
   */
  suffix?: ReactNode;
}

function WrappedFormattedValue({
  className,
  variant,
  value,
  maximumFractionDigits = 3,
  minimumFractionDigits,
  fractionDigits,
  unit,
  hideUnit = false,
  prefix,
  suffix,
}: Readonly<FormattedValueProps>): ReactElement<FormattedValueProps> | null {
  const { i18n } = useTranslation();

  if (isNil(value) || (typeof value === 'object' && isNil(value.value))) {
    return null;
  }

  const effectiveValue = isMeasure(value) ? value.value : value;
  let formatted =
    typeof effectiveValue === 'number'
      ? effectiveValue.toLocaleString(i18n.language, {
          maximumFractionDigits: fractionDigits ?? maximumFractionDigits,
          minimumFractionDigits: fractionDigits ?? minimumFractionDigits,
        })
      : effectiveValue;
  if (!hideUnit) {
    if (unit) {
      // narrow non-breaking space prevents separation of value from unit
      formatted += `\u202f${unit}`;
    } else if (typeof value === 'object') {
      formatted += `\u202f${value.unit}`;
    }
  }

  const content = (
    <>
      {prefix}
      {formatted}
      {suffix}
    </>
  );

  if (variant === 'raw') {
    return content;
  }

  return (
    <Typography variant={variant} data-testid="formatted-value" className={className}>
      {content}
    </Typography>
  );
}

export const FormattedValue = memo(WrappedFormattedValue);
